import {
  type BriefContent,
  confidenceOf,
  headlineWordingSlots,
  renderRichText,
  richTextChips,
} from '@sigora/domain';
import { Fragment, useEffect, useRef, useState } from 'react';

import {
  CANCEL_LABEL,
  EDITED_BY_YOU,
  EDIT_WORDING_HINT,
  EDIT_WORDING_LABEL,
  INFORMS_PREFIX,
  SAVE_LABEL,
  SAVING_LABEL,
  wordingSlotLabel,
} from '../brief-copy.ts';
import { type RevisionMarks, UpdatedTag } from './RevisionMarks.tsx';
import { type SendBriefEdit, briefEditRefusal } from './brief-edit.ts';

/**
 * What a block needs to take the researcher's framing edits: the send, and whether an edit is out.
 * Only the Brief step passes it; anywhere else the block is read-only.
 */
export interface BriefEdits {
  send: SendBriefEdit;
  busy: boolean;
  /** Says above the Brief that it changed, for a change a block found while it was being edited. */
  noteMoved: () => void;
  /**
   * The Verdict's unsaved words, kept by the page so they outlive the blocks while a past version
   * replaces them; `null` while the Verdict is not being edited.
   */
  verdictDraft: string[] | null;
  keepVerdictDraft: (wording: string[] | null) => void;
}

/**
 * The Verdict: the Study Type eyebrow, the headline and the line under it, the decision it informs
 * and the Confidence label with what it means. No heading of its own — it is what the page opens on.
 *
 * The label and its line are the tier and the people the Brief was published with, worded by
 * `confidenceOf`: a published Brief keeps its Confidence whatever the thresholds become, and no
 * count or threshold is worked out here.
 *
 * With `edits`, the headline's words can be edited: one field per run of words around its counts
 * (`headlineWordingSlots`), with each count drawn between them as it is stored and nothing to type
 * it into, so a save sends words and nothing else. `edited` marks a headline in the researcher's
 * words rather than Sigora's, and `marks` the Verdict a Sigora revision changed.
 *
 * The editor stays open over a Brief read again under it. Over a headline with the same number of
 * runs the typed words stay; over one whose counts now fall elsewhere they no longer fit, so the
 * fields are filled again from the new headline, the page says the Brief changed, and the focus
 * stays in the editor.
 *
 * The open editor's words are kept with `edits` as they are typed, so an editor drawn again — after
 * a past version was viewed in place of the blocks — opens on them, without taking the focus.
 */
export const Verdict = ({
  verdict,
  edited,
  edits,
  marks,
}: {
  verdict: BriefContent['verdict'];
  edited: boolean;
  edits?: BriefEdits;
  marks?: RevisionMarks | null;
}) => {
  const confidence = confidenceOf(verdict.confidence.tier, verdict.confidence.completes);
  const slots = headlineWordingSlots(verdict.headline);
  const chips = richTextChips(verdict.headline);
  const kept = edits?.verdictDraft ?? null;
  const [editing, setEditing] = useState(kept !== null);
  const [typed, setTyped] = useState<string[]>(kept ?? []);
  const [error, setError] = useState<string | null>(null);
  const firstSlot = useRef<HTMLInputElement | null>(null);
  const editButton = useRef<HTMLButtonElement | null>(null);
  const focusOnClose = useRef(false);
  const reopened = useRef(kept !== null);
  const open = edits !== undefined && editing;
  const fits = typed.length === slots.length;
  const noteMoved = edits?.noteMoved;
  const keepDraft = edits?.keepVerdictDraft;

  useEffect(() => {
    keepDraft?.(open ? typed : null);
  }, [keepDraft, open, typed]);

  useEffect(() => {
    if (reopened.current) {
      reopened.current = false;
      return;
    }
    if (open) {
      firstSlot.current?.focus();
    } else if (focusOnClose.current) {
      focusOnClose.current = false;
      editButton.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open || fits) {
      return;
    }
    setTyped(headlineWordingSlots(verdict.headline));
    noteMoved?.();
    firstSlot.current?.focus();
  }, [open, fits, verdict.headline, noteMoved]);

  const close = () => {
    focusOnClose.current = true;
    setEditing(false);
    setError(null);
  };

  /**
   * An editor opened on words a save is still carrying — kept while a past version was open and
   * drawn again on Back before the save answered — closes once the headline comes to hold exactly
   * those words, as the save that sent them would have closed it. Words typed since keep it open.
   */
  const headlineKey = JSON.stringify(slots);
  const headlineSeen = useRef(headlineKey);
  useEffect(() => {
    if (headlineSeen.current === headlineKey) {
      return;
    }
    headlineSeen.current = headlineKey;
    if (open && JSON.stringify(typed) === headlineKey) {
      close();
    }
    // Asked only on a move of the headline, which the guard above holds it to: typing is not a save
    // landing.
  }, [headlineKey, open, typed]);

  const save = async () => {
    if (edits === undefined || !open || !fits || edits.busy) {
      return;
    }

    setError(null);
    const outcome = await edits.send({ op: 'reword_verdict', wording: typed });
    if (outcome.status === 'saved') {
      close();
      return;
    }
    if (outcome.status === 'refused') {
      setError(briefEditRefusal(outcome.failure));
    }
    // Saving disabled the fields, which took the focus from them; it goes back to the words.
    firstSlot.current?.focus();
  };

  return (
    <div className="brief-verdict" data-test-id="brief-verdict">
      {verdict.eyebrow !== null && (
        <p className="brief-eyebrow" data-test-id="brief-eyebrow">
          {verdict.eyebrow}
        </p>
      )}
      {open ? (
        <div
          className="brief-wording"
          data-test-id="brief-wording"
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !edits.busy) {
              event.preventDefault();
              close();
            }
          }}
        >
          <p
            className="brief-headline brief-wording-fields"
            role="group"
            aria-label={EDIT_WORDING_LABEL}
          >
            {slots.map((_, index) => {
              const chip = chips[index];
              const words = typed[index] ?? '';

              return (
                // Positional by design: a field is the gap between two stored counts it stands in.
                // oxlint-disable-next-line react/no-array-index-key
                <Fragment key={index}>
                  <input
                    type="text"
                    className="brief-wording-slot"
                    data-test-id="brief-wording-slot"
                    aria-label={wordingSlotLabel(index + 1, slots.length)}
                    value={words}
                    size={Math.max(words.length, 3)}
                    disabled={edits.busy}
                    ref={index === 0 ? firstSlot : undefined}
                    onChange={(event) => {
                      const next = event.target.value;
                      setTyped((current) =>
                        current.map((each, at) => (at === index ? next : each)),
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                        event.preventDefault();
                        void save();
                      }
                    }}
                  />
                  {chip !== undefined && (
                    <span className="brief-wording-chip" data-test-id="brief-wording-chip">
                      {renderRichText([chip])}
                    </span>
                  )}
                </Fragment>
              );
            })}
          </p>
          <div className="brief-edit-actions">
            <button
              type="button"
              className="button-primary"
              data-test-id="brief-wording-save"
              disabled={edits.busy}
              onClick={() => void save()}
            >
              {edits.busy ? SAVING_LABEL : SAVE_LABEL}
            </button>
            <button
              type="button"
              data-test-id="brief-wording-cancel"
              disabled={edits.busy}
              onClick={close}
            >
              {CANCEL_LABEL}
            </button>
          </div>
          {error !== null && (
            <p className="chat-error" data-test-id="brief-wording-error" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <p className="brief-headline" data-test-id="brief-headline">
          {renderRichText(verdict.headline)}
        </p>
      )}
      {(edited || marks?.updated.has('verdict') === true) && (
        <p className="brief-marks">
          {edited && (
            <span className="brief-tag" data-test-id="brief-verdict-edited">
              {EDITED_BY_YOU}
            </span>
          )}
          <UpdatedTag marks={marks} block="verdict" />
        </p>
      )}
      <p className="brief-sub-verdict" data-test-id="brief-sub-verdict">
        {renderRichText(verdict.sub_verdict)}
      </p>
      <p className="brief-informs" data-test-id="brief-informs">
        {INFORMS_PREFIX}
        {verdict.informs}
      </p>
      <div className="brief-confidence" data-confidence-tier={confidence.tier}>
        <p className="brief-confidence-label" data-test-id="brief-confidence-label">
          {confidence.label}
        </p>
        <p data-test-id="brief-confidence-explanation">{confidence.explanation}</p>
      </div>
      {edits !== undefined && (
        <p className="brief-edit-hint">
          {!open && (
            <button
              type="button"
              className="text-link"
              data-test-id="brief-wording-edit"
              ref={editButton}
              disabled={edits.busy}
              onClick={() => {
                setTyped(slots);
                setEditing(true);
                setError(null);
              }}
            >
              {EDIT_WORDING_LABEL}
            </button>
          )}{' '}
          <span className="brief-meta" data-test-id="brief-wording-hint">
            {EDIT_WORDING_HINT}
          </span>
        </p>
      )}
    </div>
  );
};
